import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchOneQuestionApi, updateQuestionApi } from "src/services/question";
import { getControls, fetchDataApi } from "src/services/controls";
import { fetchAllCategoryDataApi, fetchAllDataApi } from "src/services/riskServices";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { useTranslation } from "react-i18next";
import LoadingScreen from "src/Loading/LoadingScreen";
import Select from "react-select";
import { getUserInfo, getUserRolePermissions } from 'src/services/userServicer';
import { getReferences } from 'src/services/reference';

interface QuestionFormValues {
  question_en: string;
  question_ar: string;
  question_type: string;
  categoryId: string;
  referenceId: string,

  risk: string[];
  control: string[];
  choices: string[];
}

interface Category {
  id: string;
  name: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const UpdateQuestion = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [controlsOptions, setControlsOptions] = useState<SelectOption[]>([]);
  const [riskOptions, setRiskOptions] = useState<SelectOption[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [optionsType, setOptionsType] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<QuestionFormValues>({
    defaultValues: {
      choices: [],
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch Question Data
        if (id) {
          const response = await fetchOneQuestionApi(`bank-questions/${id}`);
          if (response?.success) {
            const question = response.data;
            reset({
              question_en: question.question_en || "",
              question_ar: question.question_ar || "",
              question_type: question.question_type || "",
              categoryId: question.categoryId || "",
              risk: question.risks.map((risk: any) => risk.id),
              control: question.controls.map((control: any) => control.id),
              choices: question.choices ? question.choices.split(",") : [],
            });
            setOptionsType(question.type);
          } else {
            toast.error("Error fetching question");
          }
        }

        // Fetch Controls
        const controlsResponse:any = await getControls();
        if (controlsResponse?.success) {
          setControlsOptions(
            controlsResponse.data.map((control: any) => ({
              value: control.id,
              label: control.description,
            }))
          );
        }

        // Fetch Risks
        const risksResponse = await fetchAllDataApi();
        if (risksResponse.success) {
          setRiskOptions(
            risksResponse.data.map((risk: any) => ({
              value: risk.id,
              label: risk.name,
            }))
          );
        }

        // Fetch Categories
        const categoriesResponse = await fetchAllCategoryDataApi();
        if (categoriesResponse?.success) {
          setCategories(categoriesResponse.data);
        }
      } catch (error) {
        toast.error("Error loading data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, reset]);

  const onSubmit = async (data: QuestionFormValues) => {
    try {
      if (!id) return;

      // Ensure choices is an array before joining
      const choices = Array.isArray(watch("choices")) ? watch("choices").join(", ") : "";

      const payload = {
        en_questionText: data.question_en,
        ar_questionText: data.question_ar,
        type: data.question_type,
        options: choices, // Use the formatted choices
        category: data.categoryId,
        referenceId: data.referenceId,
        control: data.control,
        risk: data.risk,
      };
      //console.log("Payload to be sent:", payload);

      const response = await updateQuestionApi(id, payload);
      //console.log("Response from API:", response);

      if (response?.success) {
        toast.success("Question updated successfully");
        navigate("/bankQuestions");
      } else {
        toast.error(response?.message || "Failed to update question");
      }
    } catch (error: any) {
      console.error("Error updating question:", error);
      toast.error(error?.message || "An error occurred while updating the question");
    }
  };
  
  

  if (loading) return 
  <div className="root mt-10  align-item-center text-black d-flex justify-content-center">
    <LoadingScreen/>
    </div>;

  return (
    <div className="root">
      <Breadcrumb>
        <BreadcrumbItem>{t("header.you_are_here")}</BreadcrumbItem>
      <span className="mx-2">/</span> 

        <BreadcrumbItem>{t("question.Questions")}</BreadcrumbItem>
      <span className="mx-2">/</span> 
        
        <BreadcrumbItem active>{t("question.Update Question")}</BreadcrumbItem>
      </Breadcrumb>
      <h5>{t("question.Update Question")}</h5>

      <form onSubmit={handleSubmit(onSubmit)} className="create-compliance-form">
        <div className="form-section2 mb-5">
          <h5 className="section-title">{t("question.BasicInfo")}</h5>

          <div className="form-group">
            <label>{t("question.English Question")}</label>
            <input
              type="text"
              className={`form-control ${errors.question_en ? "is-invalid" : ""}`}
              {...register("question_en", { required: true })}
            />
            {errors.question_en && <div className="invalid-feedback">{t("surveys.This field is required")}</div>}
          </div>

          <div className="form-group">
            <label>{t("question.Arabic Question")}</label>
            <input
              type="text"
              className={`form-control ${errors.question_ar ? "is-invalid" : ""}`}
              {...register("question_ar", { required: true })}
            />
            {errors.question_ar && <div className="invalid-feedback">{t("surveys.This field is required")}</div>}
          </div>

          <div className="form-group ">
            <label>{t("question.Type")}</label>
            <select className="" {...register("question_type", { required: true })}>
              <option value="">{t("question.Select Type")}</option>
              <option value="textarea">{t("question.Text")}</option>
              <option value="multiple_choice">{t("question.Multiple Choice")}</option>
              <option value="single_choice">{t("question.Single Choice")}</option>
              <option value="compliance_related">{t("question.Related to compliance")}</option>
              <option value="risk_related">{t("question.Related to risk")}</option>
            </select>
            {errors.question_type && <div className="invalid-feedback">{t("surveys.This field is required")}</div>}
          </div>

          <div className="form-group">
            <label>{t("question.Category")}</label>
            <select className="" {...register("categoryId", { required: true })}>
              <option value="">{t("question.Select Category")}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{t("question.Risk")}</label>
            <Select
              options={riskOptions}
              isMulti
              classNamePrefix="react-select"
              value={riskOptions.filter(option => watch("risk").includes(option.value))}
              onChange={(selected) => setValue("risk", selected ? selected.map((s: SelectOption) => s.value) : [])}
            />
          </div>

          <div className="form-group">
            <label>{t("question.Control")}</label>
            <Select
              options={controlsOptions}
              isMulti
              classNamePrefix="react-select"
              value={controlsOptions.filter(option => watch("control").includes(option.value))}
              onChange={(selected) => setValue("control", selected ? selected.map((s: SelectOption) => s.value) : [])}
            />
          </div>

          <div className="form-group">
            <label>{t("question.Options")}</label>
            <textarea
              className={`form-control ${errors.choices ? "is-invalid" : ""}`}
              {...register("choices", { required: true })}
              placeholder={String(t("question.Options") || "Options")}
              defaultValue={Array.isArray(watch("choices")) ? watch("choices").join(", ") : ""}
            />
            {errors.choices && <div className="invalid-feedback">{t("surveys.This field is required")}</div>}
          </div>
          <button type="submit" className="submit-button4">
            {t("question.Update Question")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateQuestion;
